import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Loader, HandHeart, Heart, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HV = { h: "h-80", filter: "brightness(1.45) contrast(0.95) saturate(1.1)", pos: "center 35%", fadeH: "h-20" };
const ROOMS_HEADER = "https://customer-assets-0z36b82j.emergentagent.net/job_comunidad-duelo/artifacts/x78z4yvw_IMG_6263.jpeg";

const TOPIC_NAMES = {
  relaciones_pareja: "Relaciones de Pareja", familia: "Familia", salud_emocional: "Salud Emocional",
  fallecimiento: "Fallecimiento", trabajo: "Trabajo", adicciones: "Adicciones",
  vida_proposito: "Vida y Propósito", otros: "Otros",
};
function friendlyTopic(t) { return TOPIC_NAMES[t] || t; }

function RoomCard({ room, isAcompanado, unread, currentUser, onDelete }) {
  const navigate = useNavigate();
  // Un solo color por rol: verde Refugium para acompañado (color del logo), naranja terracota para acompañante.
  const accent = isAcompanado ? "#48734C" : "#E48D57";

  const topicNames = room.topics?.slice(0, 3).map(friendlyTopic) || [];
  const displayName = room.room_name || topicNames.slice(0, 2).join(" · ") || "Sala Refugium";
  const memberCount = (room.acompanantes?.length || 0) + (room.acompanado ? 1 : 0);
  const capacity = (room.max_acompanantes || 2) + 1;

  const creatorId = room.acompanado?.user_id || room.created_by;
  const isCreator = currentUser?.id && creatorId === currentUser.id;
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "secondary_admin";
  const canDelete = isCreator || isAdmin;

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete?.(room);
  };

  const timeLabel = () => {
    if (!room.last_message_at) return "";
    const d = new Date(room.last_message_at);
    const now = new Date();
    const diffH = (now - d) / 3600000;
    if (diffH < 1) return "hace unos minutos";
    if (diffH < 24) return `hoy · ${d.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}`;
    if (diffH < 48) return "ayer";
    if (diffH < 168) return d.toLocaleDateString("es", { weekday: "long" });
    return d.toLocaleDateString("es", { day: "numeric", month: "short" });
  };

  const hasUnread = unread > 0;

  return (
    <button
      data-testid={`room-card-${room.id}`}
      onClick={() => {
        try { sessionStorage.setItem("salas_active_tab", isAcompanado ? "acompanado" : "acompanante"); } catch {}
        navigate(`/app/salas/${room.id}`);
      }}
      className="relative w-full text-left rounded-3xl p-6 border hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col group"
      style={{
        // "Cabaña iluminada por dentro": crema cálido con un resplandor interior suave
        background: `radial-gradient(120% 90% at 20% 0%, ${accent}22 0%, transparent 55%), radial-gradient(140% 100% at 80% 100%, ${accent}18 0%, transparent 60%), #FBF4E4`,
        borderColor: `${accent}30`,
        boxShadow: `0 4px 24px rgba(91,62,46,0.10), inset 0 0 40px ${accent}10`,
      }}
    >
      {/* Sutil parpadeo de "brasa" — solo en hover */}
      <div
        className="pointer-events-none absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-3xl"
        style={{ background: `radial-gradient(circle, ${accent}55 0%, transparent 70%)` }}
      />

      {/* Header block: icon or photo, plus unread badge */}
      <div className="relative flex items-start justify-between mb-4">
        {room.image_url ? (
          <div
            className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-white/70"
            style={{ boxShadow: `0 4px 16px ${accent}40, inset 0 0 0 1px ${accent}20` }}
          >
            <img
              src={room.image_url.startsWith("http") ? room.image_url : `${process.env.REACT_APP_BACKEND_URL}${room.image_url}`}
              alt={displayName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${accent}55, ${accent}25 60%, ${accent}15)`,
              boxShadow: `0 2px 10px ${accent}30, inset 0 0 12px ${accent}20`,
            }}
          >
            <Users size={26} strokeWidth={1.5} style={{ color: accent }} />
          </div>
        )}

        {hasUnread && (
          <span
            data-testid={`room-unread-${room.id}`}
            className="min-w-[26px] h-[26px] px-2 text-white text-[11px] font-bold rounded-full flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.15)] animate-pulse"
            style={{ backgroundColor: accent }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}

        {canDelete && (
          <button
            data-testid={`room-delete-${room.id}`}
            onClick={handleDeleteClick}
            aria-label="Eliminar sala"
            title="Eliminar sala"
            className="absolute top-0 right-0 w-8 h-8 rounded-full bg-white/85 backdrop-blur-sm text-[#C44A3A] flex items-center justify-center shadow-[0_2px_10px_rgba(91,62,46,0.15)] hover:bg-[#C44A3A] hover:text-white transition-all"
          >
            <Trash2 size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Title */}
      <h3 className={`relative font-heading text-xl text-[#5B3E2E] mb-1.5 leading-tight ${hasUnread ? "font-bold" : "font-semibold"}`}>
        {displayName}
      </h3>

      {/* Topic chips (only if no custom name) */}
      {topicNames.length > 0 && !room.room_name && (
        <div className="relative flex flex-wrap gap-1.5 mb-3">
          {topicNames.map(t => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${accent}22`, color: accent }}>
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Meta line: members + last activity */}
      <div className="relative flex items-center gap-2 text-[11px] text-[#5B3E2E]/55 mb-3">
        <span className="flex items-center gap-1">
          <Users size={11} strokeWidth={2} />
          <span className="font-medium">{memberCount}<span className="opacity-50">/{capacity}</span></span>
        </span>
        {room.last_message_at && (
          <>
            <span className="w-0.5 h-0.5 rounded-full bg-[#5B3E2E]/25" />
            <span className={hasUnread ? "font-semibold" : ""} style={{ color: hasUnread ? accent : undefined }}>
              {timeLabel()}
            </span>
          </>
        )}
      </div>

      {/* Last message preview */}
      {room.last_message_preview ? (
        <p className={`relative text-sm leading-relaxed line-clamp-2 ${hasUnread ? "text-[#5B3E2E]/85 font-medium" : "text-[#5B3E2E]/60 italic"}`}>
          «{room.last_message_preview}»
        </p>
      ) : (
        <p className="relative text-sm text-[#5B3E2E]/45 italic leading-relaxed">
          Todavía nadie ha hablado. Sé quien encienda la conversación.
        </p>
      )}
    </button>
  );
}

export default function SalasPage() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTabState] = useState(() => {
    try { return sessionStorage.getItem("salas_active_tab") || "acompanado"; }
    catch { return "acompanado"; }
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    try { sessionStorage.setItem("salas_active_tab", tab); } catch {}
  };
  const [unreadByRoom, setUnreadByRoom] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const { data } = await axios.get(`${API}/rooms`, { withCredentials: true });
        setRooms(data);
      } catch (err) {
        console.error("Error loading rooms:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/unread`, { withCredentials: true });
        setUnreadByRoom(data.rooms || {});
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Se mantiene solo el fetch principal cada 15s en fetchRooms.
    // (Se retiró el contador global de personas en espera para reducir ruido visual.)
  }, []);

  const acompanadoRooms = rooms.filter(r => r.acompanado?.user_id === user?.id);
  const acompananteRooms = rooms.filter(r => r.acompanantes?.some(a => a.user_id === user?.id));
  const currentRooms = activeTab === "acompanado" ? acompanadoRooms : acompananteRooms;

  const handleDeleteRoom = async (room) => {
    const displayName = room.room_name || (room.topics?.slice(0, 2).map(t => TOPIC_NAMES[t] || t).join(" · ")) || "esta sala";
    if (!window.confirm(`¿Eliminar "${displayName}"? Se borrarán todos los mensajes. Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/rooms/${room.id}`, { withCredentials: true });
      setRooms(prev => prev.filter(r => r.id !== room.id));
      toast.success("Sala eliminada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar la sala");
    }
  };

  return (
    <div
      data-testid="salas-page"
      className="relative min-h-screen -mb-20 pb-20"
      style={{ backgroundColor: "#0d0906" }}
    >
      <div className="relative max-w-lg mx-auto">

      {/* Header Image - Cabaña con hoguera */}
      <div className="relative">
        <div className={`relative overflow-hidden ${HV.h}`}>
          <img src={ROOMS_HEADER} alt="Salas Refugium" className="w-full h-full object-cover" style={{ filter: HV.filter, objectPosition: HV.pos }} />
          {/* Fundido inferior hacia el color exacto del fondo — sin corte visible */}
          <div className={`absolute inset-x-0 bottom-0 pointer-events-none ${HV.fadeH}`} style={{ background: "linear-gradient(to bottom, rgba(13,9,6,0) 0%, rgba(13,9,6,0.7) 65%, #0d0906 100%)" }} />
        </div>

        <div className="absolute -bottom-7 left-5 right-24 h-14 flex items-center pointer-events-none">
          <h1 data-testid="salas-title" className="font-heading text-2xl text-white font-semibold tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">Salas Refugium</h1>
        </div>

        {/* Botón flotante "+" para unirse a una nueva sala */}
        <button
          data-testid="join-room-btn"
          onClick={() => navigate("/app/salas/join")}
          aria-label="Unirse a una nueva sala"
          title="Unirse a una nueva sala"
          className="absolute -bottom-7 right-5 w-14 h-14 rounded-full bg-white text-[#5B3E2E] flex items-center justify-center shadow-[0_6px_18px_rgba(196,74,58,0.55)] hover:shadow-[0_10px_28px_rgba(228,141,87,0.7)] hover:-translate-y-0.5 transition-all"
        >
          <Plus size={26} strokeWidth={2.2} />
        </button>
      </div>

      <div className="relative px-4 pt-12 pb-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader size={24} className="text-[#E48D57] animate-spin" />
          </div>
        ) : acompanadoRooms.length === 0 && acompananteRooms.length === 0 ? (
          /* Empty state global — sin tabs, con CTA acogedor que abre la selección de rol */
          <button
            data-testid="empty-salas-cta"
            onClick={() => navigate("/app/salas/join")}
            className="w-full text-center py-6 px-6 bg-[#FDFAF4]/95 backdrop-blur-sm rounded-3xl border border-[#E48D57]/20 shadow-[0_8px_30px_rgba(0,0,0,0.3),inset_0_0_60px_rgba(228,141,87,0.10)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.4),inset_0_0_80px_rgba(228,141,87,0.16)] hover:-translate-y-0.5 transition-all group"
          >
            <p className="font-heading text-lg text-[#5B3E2E] leading-tight mb-2">
              Aquí hay una comunidad esperándote
            </p>
            <p className="text-sm text-[#5B3E2E]/70 leading-relaxed max-w-[320px] mx-auto">
              Crea tu primera sala y comparte lo que llevas dentro para ser acompañad@, o únete a las salas de otros miembros para acompañarles en su camino.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#E48D57] group-hover:text-[#D07C46]">
              Entrar a una sala
              <Plus size={14} strokeWidth={2.5} />
            </span>
          </button>
        ) : (
          <>
            {/* Tab Switcher — cream con contraste sobre fondo oscuro */}
            <div className="flex gap-1.5 bg-[#FDFAF4]/95 backdrop-blur-sm p-1.5 rounded-2xl border border-[#E48D57]/25 shadow-[0_4px_20px_rgba(0,0,0,0.35),0_0_30px_#E48D5720]">
              <button
                data-testid="tab-acompanado"
                onClick={() => setActiveTab("acompanado")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeTab === "acompanado"
                    ? "bg-[#48734C] text-white shadow-[0_2px_10px_rgba(72,115,76,0.45)]"
                    : "text-[#5B3E2E]/50 hover:text-[#5B3E2E]/80 hover:bg-[#5B3E2E]/5"
                }`}
              >
                <Heart size={14} />
                <span>Acompañad@</span>
                {acompanadoRooms.length > 0 && (
                  <span className={`text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${activeTab === "acompanado" ? "bg-white/25" : "bg-[#48734C]/15 text-[#48734C]"}`}>
                    {acompanadoRooms.length}
                  </span>
                )}
              </button>
              <button
                data-testid="tab-acompanante"
                onClick={() => setActiveTab("acompanante")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeTab === "acompanante"
                    ? "bg-[#E48D57] text-white shadow-[0_2px_10px_rgba(217,120,51,0.45)]"
                    : "text-[#5B3E2E]/50 hover:text-[#5B3E2E]/80 hover:bg-[#5B3E2E]/5"
                }`}
              >
                <HandHeart size={14} />
                <span>Acompañante</span>
                {acompananteRooms.length > 0 && (
                  <span className={`text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${activeTab === "acompanante" ? "bg-white/25" : "bg-[#E48D57]/15 text-[#E48D57]"}`}>
                    {acompananteRooms.length}
                  </span>
                )}
              </button>
            </div>

            {/* Room List — para la pestaña activa (una de las dos tiene salas) */}
            {currentRooms.length === 0 ? (
              <div className="text-center py-14 bg-[#FDFAF4]/95 backdrop-blur-sm rounded-3xl border border-[#E48D57]/20 shadow-[0_8px_30px_rgba(0,0,0,0.3),inset_0_0_50px_rgba(228,141,87,0.08)]">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{
                    background: activeTab === "acompanado"
                      ? "radial-gradient(circle, #48734C22, #48734C10)"
                      : "radial-gradient(circle, #E48D5722, #E48D5710)",
                  }}>
                  {activeTab === "acompanado"
                    ? <Heart size={24} strokeWidth={1.5} className="text-[#48734C]/60" />
                    : <HandHeart size={24} strokeWidth={1.5} className="text-[#E48D57]/60" />}
                </div>
                <p className="text-[#5B3E2E]/70 text-sm font-medium">
                  {activeTab === "acompanado"
                    ? "Todavía no eres acompañad@ en ninguna sala"
                    : "Todavía no acompañas a nadie en ninguna sala"}
                </p>
                <button
                  onClick={() => navigate("/app/salas/join")}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#E48D57] hover:text-[#D07C46]"
                >
                  Únete a tu primera sala
                  <Plus size={14} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {currentRooms.map((room, i) => (
                  <div key={room.id} className="animate-slide-up" style={{ animationDelay: `${i * 0.06}s`, opacity: 0, animationFillMode: "forwards" }}>
                    <RoomCard room={room} isAcompanado={activeTab === "acompanado"} unread={unreadByRoom[room.id] || 0} currentUser={user} onDelete={handleDeleteRoom} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
